import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useMemo } from 'react';
import type { Theme } from '@mui/material/styles';
import { getScoreColors } from './score.utils';

interface ScoringExplainerContentProps {
    html: string;
    score?: number | null;
}

/**
 * Maps hardcoded Trimoji HTML colors to their theme-aware equivalents.
 * Trimoji generates HTML with light-mode-only inline styles, so we replace them
 * before rendering to ensure correct appearance in both light and dark themes.
 */
function transformExplainerHtml(html: string, theme: Theme, scoreColor?: string): string {
    const divider = theme.palette.divider;
    const primary = theme.palette.primary.main;
    const error = theme.palette.error.main;
    const textPrimary = theme.palette.text.primary;
    const textSecondary = theme.palette.text.secondary;
    const bgHover = alpha(theme.palette.text.primary, 0.04);
    const bgSelected = alpha(theme.palette.text.primary, 0.08);
    // Trimoji orange score colour — replace with our score-mapped colour
    const scoreHighlight = scoreColor ?? theme.palette.warning.main;

    return html
        // General dark text → primary text
        .replace(/color:\s*#333\b/gi, `color: ${textPrimary}`)
        // Medium grey labels → secondary text
        .replace(/color:\s*#555\b/gi, `color: ${textSecondary}`)
        .replace(/color:\s*#666\b/gi, `color: ${textSecondary}`)
        // Blue section headers → primary
        .replace(/color:\s*#1976d2\b/gi, `color: ${primary}`)
        // Red "not found" / low scores → error
        .replace(/color:\s*#d32f2f\b/gi, `color: ${error}`)
        // Trimoji orange score highlight variants (MUI warning palette) → our score colour
        .replace(/color:\s*#ed6c02\b/gi, `color: ${scoreHighlight}`)
        .replace(/color:\s*#ff9800\b/gi, `color: ${scoreHighlight}`)
        .replace(/color:\s*#f57c00\b/gi, `color: ${scoreHighlight}`)
        .replace(/color:\s*#e65100\b/gi, `color: ${scoreHighlight}`)
        .replace(/color:\s*#fb8c00\b/gi, `color: ${scoreHighlight}`)
        // Light card background → subtle hover surface
        .replace(/background-color:\s*#f9f9f9\b/gi, `background-color: ${bgHover}`)
        // Table header background → selected surface
        .replace(/background-color:\s*#f5f5f5\b/gi, `background-color: ${bgSelected}`)
        // Table / card borders
        .replace(/border:\s*1px\s+solid\s+#ddd\b/gi, `border: 1px solid ${divider}`)
        // Coloured border-bottom on section headers
        .replace(/border-bottom:\s*2px\s+solid\s+#1976d2\b/gi, `border-bottom: 2px solid ${primary}`)
        // font-family override (Trimoji uses Arial)
        .replace(/font-family:[^;"]*/gi, `font-family: ${theme.typography.fontFamily}`);
}

const ScoringExplainerContent: React.FC<ScoringExplainerContentProps> = ({ html, score }) => {
    const theme = useTheme();
    const scoreColor = score != null ? getScoreColors(score).color : undefined;

    const transformedHtml = useMemo(
        () => transformExplainerHtml(html, theme, scoreColor),
        // theme.palette.mode ensures re-transform on theme switch
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [html, theme.palette.mode, scoreColor],
    );

    return (
        <Box
            component="div"
            dangerouslySetInnerHTML={{ __html: transformedHtml }}
            sx={{
                color: theme.palette.text.primary,
                lineHeight: 1.6,
                // Catch-all: any remaining hardcoded colours that weren't matched
                // by the regex transform are overridden here via !important so
                // the content is always readable against the current background.
                '& *': {
                    fontFamily: `${theme.typography.fontFamily} !important`,
                },
                // Tables
                '& table': {
                    width: '100%',
                    borderCollapse: 'collapse',
                },
                '& th, & td': {
                    border: `1px solid ${theme.palette.divider}`,
                },
                '& th': {
                    backgroundColor: alpha(theme.palette.text.primary, 0.08),
                    color: theme.palette.text.primary,
                },
            }}
        />
    );
};

export default ScoringExplainerContent;
